import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import { formatKabulDisplay } from "./ScheduleFields";
import type { StatusTone } from "../lib/overview-ops";
import {
  OBSERVABILITY_AGGREGATE_DISCLAIMER_UI_FA,
  OBSERVABILITY_NO_LIVE_OPS_NOTE,
  OBSERVABILITY_PRIVACY_BANNER_FA,
  alertLabelFa,
  alertSeverityTone,
  alertStatusDisplay,
  drillResultDisplay,
  formatSafeAlertDetails,
  relatedObservabilityLinks,
  safeAlertDetailEntries,
  truncateSha256,
  type AggregateSortKey,
  type AlertSortKey,
  type DrillSortKey,
  type ObservabilityAggregateListRow,
  type ObservabilityAlertRow,
  type ObservabilityDrillRow,
} from "../lib/observability-ops";
import { cmsStrings } from "../terminology";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

/**
 * Privacy banner — Dari only (L3A).
 * API may still return disclaimerEn for contract tests; do not render it here.
 */
export function ObservabilityPrivacyBanner({
  disclaimerFa,
}: {
  disclaimerFa?: string;
}) {
  const aggregateNote = disclaimerFa?.trim() || OBSERVABILITY_AGGREGATE_DISCLAIMER_UI_FA;
  return (
    <aside
      className="ops-safety-summary ops-safety-summary--privacy"
      role="note"
      data-vt-obs-privacy
      aria-labelledby="obs-privacy-title"
    >
      <p className="ops-safety-summary__eyebrow">{cmsStrings.observability.privacyKind}</p>
      <h2 id="obs-privacy-title" className="ops-safety-summary__title">
        {cmsStrings.observability.privacyHeading}
      </h2>
      <ul className="ops-safety-summary__points">
        <li>بدون نام کاربری، آدرس MAC، IP، یا User-Agent</li>
        <li>بدون اعتبارنامه، دادهٔ نشست، یا لاگ خام</li>
      </ul>
      <details className="ops-safety-disclosure">
        <summary>متن کامل حریم خصوصی و محدودیت‌های پایش</summary>
        <p>{OBSERVABILITY_PRIVACY_BANNER_FA}</p>
        <p>{aggregateNote}</p>
        <p>{OBSERVABILITY_NO_LIVE_OPS_NOTE}</p>
      </details>
    </aside>
  );
}

function RelatedLinks() {
  const links = relatedObservabilityLinks();
  return (
    <nav className="content-inspect__links" aria-label="مسیرهای مرتبط پایش">
      {links.map((link) => (
        <Link key={link.id} to={link.to} className="btn btn--ghost btn--sm">
          {link.label}
        </Link>
      ))}
    </nav>
  );
}

/* ─── Filters ─── */

type AlertFilterProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: AlertSortKey;
  onSortChange: (value: AlertSortKey) => void;
};

export function ObservabilityAlertFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: AlertFilterProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "firing", label: "ارسال‌شده" },
    { id: "skipped", label: "بدون مقصد" },
    { id: "failed", label: "ناموفق/مسدود" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر هشدار"
    >
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر هشدار"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجوی هشدار</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="نام هشدار، شدت، وضعیت…"
          aria-label="جستجوی تاریخچه هشدار"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as AlertSortKey)}
          aria-label="مرتب‌سازی هشدارها"
        >
          <option value="fired">زمان</option>
          <option value="alert">هشدار</option>
          <option value="severity">شدت</option>
          <option value="status">وضعیت</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

type AggregateFilterProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: AggregateSortKey;
  onSortChange: (value: AggregateSortKey) => void;
};

export function ObservabilityAggregateFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: AggregateFilterProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "5xx", label: "۵xx" },
    { id: "sponsored", label: "آگهی‌دار" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر شمارنده"
    >
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر شمارنده"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجوی شمارنده</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="SITE_CODE، ساعت UTC، وضعیت…"
          aria-label="جستجوی شمارنده‌ها"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as AggregateSortKey)}
          aria-label="مرتب‌سازی شمارنده‌ها"
        >
          <option value="bucket">ساعت</option>
          <option value="site">سایت</option>
          <option value="status">وضعیت</option>
          <option value="count">تعداد</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

type DrillFilterProps = {
  filter: string;
  onFilterChange: (value: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sort: DrillSortKey;
  onSortChange: (value: DrillSortKey) => void;
};

export function ObservabilityDrillFilterBar({
  filter,
  onFilterChange,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: DrillFilterProps) {
  const chips: Array<{ id: string; label: string }> = [
    { id: "all", label: "همه" },
    { id: "ok", label: "موفق" },
    { id: "fail", label: "ناموفق" },
  ];
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label="فیلتر تمرین بازیابی"
    >
      <div
        className="filter-toolbar__chips announcements-filter-bar__chips"
        role="group"
        aria-label="فیلتر تمرین بازیابی"
      >
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn btn--sm ${filter === c.id ? "btn--primary" : "btn--ghost"}`}
            onClick={() => onFilterChange(c.id)}
            aria-pressed={filter === c.id}
          >
            {c.label}
          </button>
        ))}
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">جستجوی تمرین</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="منبع، خلاصه، basename، SHA…"
          aria-label="جستجوی تمرین بازیابی"
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>مرتب‌سازی</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as DrillSortKey)}
          aria-label="مرتب‌سازی تمرین‌ها"
        >
          <option value="recorded">زمان</option>
          <option value="ok">نتیجه</option>
          <option value="duration">مدت</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

/* ─── Inspect ─── */

export function ObservabilityAlertInspectSummary({
  item,
  actions,
}: {
  item: ObservabilityAlertRow;
  actions?: ReactNode;
}) {
  const severity = alertSeverityTone(item.severity);
  const status = alertStatusDisplay(item.status);
  const details = safeAlertDetailEntries(item.details);

  return (
    <section className="content-inspect" aria-labelledby="obs-alert-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">پایش · هشدار</p>
          <h2 id="obs-alert-inspect-title" className="content-inspect__name">
            {alertLabelFa(item.alert)}
          </h2>
          <p className="content-inspect__id">
            شناسه{" "}
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.id}
            </bdi>
          </p>
        </div>
        <div className="content-inspect__badges" aria-label="وضعیت هشدار">
          <span className={`state-badge ${TONE_BADGE[severity]}`}>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.severity}
            </bdi>
          </span>
          <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
        </div>
      </header>
      {status.detail && <p className="content-inspect__eligibility-detail">{status.detail}</p>}
      <dl className="content-inspect__facts">
        <div>
          <dt>کد هشدار</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.alert}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>زمان (کابل)</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {formatKabulDisplay(item.firedAt)}
            </bdi>
          </dd>
        </div>
        {details.map((d) => (
          <div key={d.key}>
            <dt>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {d.key}
              </bdi>
            </dt>
            <dd>
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {d.value}
              </bdi>
            </dd>
          </div>
        ))}
        {details.length === 0 && (
          <div>
            <dt>جزئیات ایمن</dt>
            <dd>—</dd>
          </div>
        )}
      </dl>
      <RelatedLinks />
      {actions && <div className="content-inspect__actions">{actions}</div>}
    </section>
  );
}

export function ObservabilityAggregateInspectSummary({
  item,
}: {
  item: ObservabilityAggregateListRow;
}) {
  return (
    <section className="content-inspect" aria-labelledby="obs-agg-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">پایش · شمارنده</p>
          <h2 id="obs-agg-inspect-title" className="content-inspect__name">
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.siteCode}
            </bdi>
          </h2>
        </div>
        <div className="content-inspect__badges">
          <span className="state-badge state-badge--scheduled">
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.httpStatus}
            </bdi>
          </span>
          {item.sponsored === 1 && (
            <span className="state-badge state-badge--published">آگهی‌دار</span>
          )}
        </div>
      </header>
      <dl className="content-inspect__facts">
        <div>
          <dt>ساعت (UTC)</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.bucketUtc.replace("T", " ").slice(0, 16)}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>تعداد</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.count}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>شناسه سازمان (بدون دادهٔ کاربر)</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.organizationId}
            </bdi>
          </dd>
        </div>
      </dl>
      <RelatedLinks />
    </section>
  );
}

export function ObservabilityDrillInspectSummary({ item }: { item: ObservabilityDrillRow }) {
  const result = drillResultDisplay(item.ok);
  return (
    <section className="content-inspect" aria-labelledby="obs-drill-inspect-title">
      <header className="content-inspect__header">
        <div className="content-inspect__identity">
          <p className="content-inspect__kind">پایش · تمرین بازیابی</p>
          <h2 id="obs-drill-inspect-title" className="content-inspect__name">
            {result.label}
          </h2>
          <p className="content-inspect__id">
            شناسه{" "}
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.id}
            </bdi>
          </p>
        </div>
        <div className="content-inspect__badges">
          <span className={`state-badge ${TONE_BADGE[result.tone]}`}>{result.label}</span>
          {item.isolated && (
            <span className="state-badge state-badge--scheduled">ایزوله</span>
          )}
        </div>
      </header>
      {result.detail && <p className="content-inspect__eligibility-detail">{result.detail}</p>}
      <dl className="content-inspect__facts">
        <div>
          <dt>زمان</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {formatKabulDisplay(item.recordedAt)}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>منبع</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.source}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>خلاصه شواهد</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.evidenceSummary}
            </bdi>
          </dd>
        </div>
        <div>
          <dt>basename پشتیبان</dt>
          <dd>
            {item.backupBasename ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.backupBasename}
              </bdi>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>SHA-256 (کامل در صورت موجود)</dt>
          <dd>
            {item.backupSha256 ? (
              <bdi dir="ltr" className="ltr-isolate latin-digits">
                {item.backupSha256}
              </bdi>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>مدت</dt>
          <dd>
            <bdi dir="ltr" className="ltr-isolate latin-digits">
              {item.durationMs} ms
            </bdi>
          </dd>
        </div>
      </dl>
      <RelatedLinks />
    </section>
  );
}

/* ─── Lists ─── */

export function ObservabilityAlertInventoryList({
  rows,
  selectedId,
  onSelect,
}: {
  rows: ObservabilityAlertRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول هشدارها"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">زمان</th>
              <th scope="col">هشدار</th>
              <th scope="col">شدت</th>
              <th scope="col">وضعیت</th>
              <th scope="col">جزئیات ایمن</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const severity = alertSeverityTone(row.severity);
              const status = alertStatusDisplay(row.status);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.firedAt)}</LtrIsolate>
                    </button>
                  </td>
                  <td>{alertLabelFa(row.alert)}</td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[severity]}`}>
                      <LtrIsolate mono>{row.severity}</LtrIsolate>
                    </span>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={formatSafeAlertDetails(row.details)}
                    >
                      <LtrIsolate mono>{formatSafeAlertDetails(row.details)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های هشدار"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const severity = alertSeverityTone(row.severity);
          const status = alertStatusDisplay(row.status);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">{alertLabelFa(row.alert)}</span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[severity]}`}>
                    <bdi dir="ltr" className="ltr-isolate latin-digits">
                      {row.severity}
                    </bdi>
                  </span>
                  <span className={`state-badge ${TONE_BADGE[status.tone]}`}>{status.label}</span>
                </span>
                <span className="sites-inventory__muted">
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {formatKabulDisplay(row.firedAt)}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {formatSafeAlertDetails(row.details)}
                  </bdi>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function ObservabilityAggregateInventoryList({
  rows,
  selectedKey,
  onSelect,
  showOrg,
}: {
  rows: ObservabilityAggregateListRow[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  showOrg: boolean;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول شمارنده‌ها"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">ساعت (UTC)</th>
              <th scope="col">سایت</th>
              {showOrg && <th scope="col">سازمان</th>}
              <th scope="col">وضعیت</th>
              <th scope="col">آگهی</th>
              <th scope="col">تعداد</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.key === selectedKey;
              return (
                <tr
                  key={row.key}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.key)}
                      aria-pressed={selected}
                    >
                      <LtrIsolate mono>
                        {row.bucketUtc.replace("T", " ").slice(0, 16)}
                      </LtrIsolate>
                    </button>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.siteCode}>
                      <LtrIsolate mono>{row.siteCode}</LtrIsolate>
                    </div>
                  </td>
                  {showOrg && (
                    <td>
                      <div className="inventory-cell-truncate" title={row.organizationId}>
                        <LtrIsolate mono>{row.organizationId}</LtrIsolate>
                      </div>
                    </td>
                  )}
                  <td>
                    <div className="inventory-cell-truncate" title={String(row.httpStatus)}>
                      <LtrIsolate mono>{row.httpStatus}</LtrIsolate>
                    </div>
                  </td>
                  <td>{row.sponsored === 1 ? "بله" : "خیر"}</td>
                  <td>
                    <div className="inventory-cell-truncate" title={String(row.count)}>
                      <LtrIsolate mono>{row.count}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.key)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های شمارنده"
      >
        {rows.map((row) => {
          const selected = row.key === selectedKey;
          return (
            <li
              key={row.key}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.key)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.siteCode}
                  </bdi>
                  {" · "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.httpStatus}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.bucketUtc.replace("T", " ").slice(0, 16)}
                  </bdi>
                  {" · تعداد "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.count}
                  </bdi>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

export function ObservabilityDrillInventoryList({
  rows,
  selectedId,
  onSelect,
}: {
  rows: ObservabilityDrillRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label="جدول تمرین بازیابی"
      >
        <table className="data-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">زمان</th>
              <th scope="col">نتیجه</th>
              <th scope="col">منبع</th>
              <th scope="col">خلاصه</th>
              <th scope="col">هش</th>
              <th scope="col">مدت</th>
              <th scope="col">
                <span className="visually-hidden">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const result = drillResultDisplay(row.ok);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                    >
                      <LtrIsolate mono>{formatKabulDisplay(row.recordedAt)}</LtrIsolate>
                    </button>
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[result.tone]}`}>{result.label}</span>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.source}>
                      <LtrIsolate mono>{row.source}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.evidenceSummary}>
                      <LtrIsolate mono>{row.evidenceSummary}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={row.backupSha256 ?? undefined}
                    >
                      <LtrIsolate mono>{truncateSha256(row.backupSha256)}</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={`${row.durationMs} ms`}>
                      <LtrIsolate mono>{row.durationMs} ms</LtrIsolate>
                    </div>
                  </td>
                  <td>
                    <div className="list-row__actions list-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onSelect(row.id)}
                      >
                        جزئیات
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label="کارت‌های تمرین بازیابی"
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const result = drillResultDisplay(row.ok);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
              >
                <span className="sites-inventory-card__title">{result.label}</span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[result.tone]}`}>{result.label}</span>
                </span>
                <span className="sites-inventory__muted">
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {formatKabulDisplay(row.recordedAt)}
                  </bdi>
                </span>
                <span className="sites-inventory__muted">
                  SHA:{" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {truncateSha256(row.backupSha256)}
                  </bdi>
                  {" · "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    {row.durationMs} ms
                  </bdi>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}
